import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Lightbulb, PenLine, Trash2, CheckCircle2, Archive, Eye, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { Button } from "../../components/ui/button";

const TABS = [
  { id: "all", pt: "Tudo", en: "All", icon: Mail },
  { id: "contact", pt: "Contactos", en: "Contacts", icon: Mail },
  { id: "suggestion", pt: "Sugestões", en: "Suggestions", icon: Lightbulb },
  { id: "contribution", pt: "Contribuições", en: "Contributions", icon: PenLine },
];
const TYPE_ICON = { contact: Mail, suggestion: Lightbulb, contribution: PenLine };
const STATUS_STYLE = {
  new: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  archived: "bg-slate-500/15 text-slate-400 border-slate-500/40",
};

export default function InboxTab({ onChanged }) {
  const { t, lang } = useLang();
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = tab === "all" ? "" : `?type=${tab}`;
      const { data } = await api.get(`/admin/submissions${q}`);
      setItems(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, fn, msg) => {
    setBusy(id);
    try { await fn(); toast.success(msg); await load(); onChanged && onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const patch = (s, body, msg) => act(s.id, () => api.patch(`/admin/submissions/${s.id}`, body), msg);
  const del = (s) => act(s.id, () => api.delete(`/admin/submissions/${s.id}`), t("Removido", "Deleted"));
  const fmt = (d) => new Date(d).toLocaleString(lang === "pt" ? "pt-PT" : "en-GB");

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <h2 className="font-heading text-2xl font-bold mr-auto">{t("Mensagens", "Inbox")}</h2>
        {TABS.map((x) => (
          <button key={x.id} data-testid={`inbox-tab-${x.id}`} onClick={() => setTab(x.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${tab === x.id ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}>
            {lang === "pt" ? x.pt : x.en}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-muted-foreground">{t("A carregar…", "Loading…")}</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-card p-12 text-center text-muted-foreground">{t("Sem mensagens.", "No messages.")}</div>
      ) : (
        <div className="grid gap-4">
          {items.map((s) => {
            const Icon = TYPE_ICON[s.type] || Mail;
            const isBusy = busy === s.id;
            return (
              <div key={s.id} data-testid={`submission-${s.id}`} className={`rounded-2xl border bg-card p-5 ${s.read ? "border-white/10" : "border-primary/40"}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"><Icon className="h-3.5 w-3.5" />{s.type}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[s.status] || ""}`}>{s.status}</span>
                      {!s.read && <span className="rounded-full bg-primary/20 text-primary px-2 py-0.5 text-xs font-semibold">{t("novo", "new")}</span>}
                    </div>
                    <h3 className="mt-2 font-heading text-lg font-semibold">{s.subject}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {s.email || s.name || "—"} · {fmt(s.created_at)}
                    </p>
                    <p className="mt-3 text-sm text-slate-300 whitespace-pre-wrap">{s.message}</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {!s.read && (
                    <Button data-testid={`mark-read-${s.id}`} size="sm" variant="secondary" className="gap-1.5" onClick={() => patch(s, { read: true }, t("Marcado como lido", "Marked as read"))} disabled={isBusy}>
                      <Eye className="h-4 w-4" />{t("Marcar como lido", "Mark as read")}
                    </Button>
                  )}
                  {s.type === "suggestion" && s.status !== "approved" && (
                    <Button data-testid={`approve-suggestion-${s.id}`} size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-500" onClick={() => patch(s, { status: "approved", read: true }, t("Publicado na comunidade", "Published to community"))} disabled={isBusy}>
                      {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{t("Aprovar (mostrar no site)", "Approve (show on site)")}
                    </Button>
                  )}
                  {s.status !== "archived" && (
                    <Button data-testid={`archive-${s.id}`} size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => patch(s, { status: "archived", read: true }, t("Arquivado", "Archived"))} disabled={isBusy}>
                      <Archive className="h-4 w-4" />{t("Arquivar", "Archive")}
                    </Button>
                  )}
                  <Button data-testid={`delete-submission-${s.id}`} size="sm" variant="ghost" className="gap-1.5 text-destructive hover:text-destructive" onClick={() => del(s)} disabled={isBusy}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
